import type { TFunction } from "i18next";

import {
  compareIsoWeek,
  WEEKLY_DATA_STALE_HOURS,
  type IsoWeek,
  type WeeklyBillingKind,
  type WeeklyBudgetRow,
  type WeeklyLead,
  type WeeklyTodo,
} from "@msc/shared";

/** „KW 41/2026" bzw. „CW 41/2026" — über `weekly.weekLabel` übersetzt. */
export function kwLabel(t: TFunction, w: IsoWeek): string {
  return t("weekly.weekLabel", { week: w.isoWeek, year: w.isoYear });
}

/** `null` = allgemeine Todos (ohne Zeilenbezug). */
export function todosFuerZeile(
  todos: WeeklyTodo[],
  row: { customerId: string | null; prospectId: string | null } | null,
): WeeklyTodo[] {
  if (row === null) return todos.filter((t) => t.customerId === null && t.prospectId === null);
  if (row.customerId) return todos.filter((t) => t.customerId === row.customerId);
  return todos.filter((t) => t.prospectId === row.prospectId);
}

const BEREICHSSICHT_ROLLEN = new Set(["bereichsleiter", "stellvertreter", "geschaeftsleitung", "admin"]);

/** Rolle mit Blick über mehrere Leads (Übersicht als Einstieg auf `/weekly`). */
export function hatBereichssicht(user: { roles: Array<{ role: string }> } | null): boolean {
  return user?.roles.some((r) => BEREICHSSICHT_ROLLEN.has(r.role)) ?? false;
}

/**
 * Einstieg auf `/weekly` (Entscheidung von `WeeklyPage`):
 * - mit `employeeId` → `"lead"`: das Weekly des Paares,
 * - ohne `employeeId`, solange der Nutzer lädt → `"laden"` (sonst leitete die
 *   Lead-Seite eine Führungskraft kurz auf den ersten Lead weiter),
 * - ohne `employeeId` mit Bereichssicht → `"uebersicht"`,
 * - sonst → `"lead"` (dort `weiterleitungsZiel` aufs eigene Paar).
 */
export function einstieg(
  user: { roles: Array<{ role: string }> } | null | undefined,
  isLoading: boolean,
  search: { employeeId?: string },
): "laden" | "uebersicht" | "lead" {
  if (search.employeeId) return "lead";
  if (isLoading) return "laden";
  return hatBereichssicht(user ?? null) ? "uebersicht" : "lead";
}

/**
 * Ziel der Weiterleitung (mit `replace`) auf der Lead-Seite ohne Auswahl: das
 * erste eigene Paar, sonst der erste Eintrag; `null` = nicht weiterleiten.
 */
export function weiterleitungsZiel(
  search: { employeeId?: string },
  leads: WeeklyLead[] | undefined,
  employeeId: string | undefined,
): { employeeId: string; segmentId: string } | null {
  if (search.employeeId || !leads?.length) return null;
  const eigen = leads.find((l) => l.employeeId === employeeId) ?? leads[0]!;
  return { employeeId: eigen.employeeId, segmentId: eigen.segmentId };
}

/** Todo stammt aus einer früheren Woche (dann zeigt die Liste „seit KW …"). */
export function istUebertragen(todo: WeeklyTodo, aktuelle: IsoWeek): boolean {
  return compareIsoWeek(todo.createdIn, aktuelle) < 0;
}

/** ------------------------------------------------------------ Kennzahlen (Scheibe 3) */

const LEER = "–";
const MINUTE_MS = 60_000;
const STUNDE_MS = 60 * MINUTE_MS;
const TAG_MS = 24 * STUNDE_MS;

const euro = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const prozent = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 });
const tage = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 });
const zeitpunkt = new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeStyle: "short" });

/** Intl setzt vor „€" ein geschütztes Leerzeichen — hier ein normales, damit Anzeige und Tests gleich lesen. */
export function formatEuro(n: number | null): string {
  return n === null ? LEER : euro.format(n).replace(/\u00a0/g, " ");
}

export function formatProzent(n: number | null): string {
  return n === null ? LEER : `${prozent.format(n)} %`;
}

/**
 * Dauer einer Ticket-Kennzahl — **eine Kennzahl, eine Einheit**, unabhängig
 * vom Wert: Antwortzeiten (SLA-Geschäftszeit wie Kalender-Näherung) immer in
 * „x h y min" (ein Geschäftszeit-Tag ist kein 24-h-Tag), die Lösungsdauer
 * immer in Kalendertagen mit einer Nachkommastelle (Werte über 0, aber unter
 * 0,1 Tagen als „< 0,1 Tage"). Einheitenwörter über `t`
 * (`weekly.metrics.durationHm` / `durationDays`), Zahlen de-DE wie im Rest der App.
 */
export function formatDauer(t: TFunction, ms: number | null, einheit: "stunden" | "tage"): string {
  if (ms === null) return LEER;
  if (einheit === "tage") {
    const roh = ms / TAG_MS;
    // Sonst würde eine echte, sehr kurze Lösungsdauer als „0 Tage" erscheinen.
    if (roh > 0 && roh < 0.1) return t("weekly.metrics.durationDaysBelow", { n: tage.format(0.1) });
    const gerundet = Math.round((ms / TAG_MS) * 10) / 10;
    return t("weekly.metrics.durationDays", { count: gerundet, n: tage.format(gerundet) });
  }
  // Erst auf Minuten runden, dann zerlegen — nie „0 h 60 min".
  const minutenGesamt = Math.round(ms / MINUTE_MS);
  return t("weekly.metrics.durationHm", { h: Math.floor(minutenGesamt / 60), m: minutenGesamt % 60 });
}

/**
 * Hinweis unter der Budgettabelle: Projekte mit Budget, aber ohne Restbudget
 * stehen in der Liste, zählen aber nicht in die Summen Budget/Restbudget und den
 * Gesamt-Prozentwert (API `summiereBudget`). `null`, wenn es keine solchen gibt.
 */
export function budgetSummenHinweis(
  t: TFunction,
  rows: Array<Pick<WeeklyBudgetRow, "budgetEur" | "budgetLeftEur">>,
): string | null {
  const count = rows.filter((r) => r.budgetEur !== null && r.budgetLeftEur === null).length;
  return count === 0 ? null : t("weekly.metrics.totalsExcludeNoLeft", { count });
}

const BLOCK_REIHENFOLGE: WeeklyBillingKind[] = ["kontingent", "pauschale", "sonstige"];

/**
 * Zeilen der Budgetübersicht nach Block (#333), in fester Reihenfolge, leere
 * Blöcke fehlen. `null` für Snapshots von vor #333 (keine Zeile kennt ihre
 * Abrechnungsart) — die zeigt die Oberfläche weiter als eine Tabelle.
 */
export function budgetBloecke(
  rows: WeeklyBudgetRow[],
): Array<{ art: WeeklyBillingKind; rows: WeeklyBudgetRow[] }> | null {
  if (rows.every((r) => r.billingKind === undefined)) return null;
  return BLOCK_REIHENFOLGE.map((art) => ({ art, rows: rows.filter((r) => (r.billingKind ?? "sonstige") === art) })).filter(
    (b) => b.rows.length > 0,
  );
}

/** Datenstand fehlt oder ist älter als `WEEKLY_DATA_STALE_HOURS`. */
export function istVeraltet(iso: string | null, jetzt: Date): boolean {
  if (iso === null) return true;
  return jetzt.getTime() - new Date(iso).getTime() > WEEKLY_DATA_STALE_HOURS * STUNDE_MS;
}

/**
 * Bezugszeit des Datenstand-Hinweises: offen gegen „jetzt", abgeschlossen gegen
 * den Stichtag der eingefrorenen Kennzahlen (`metrics.asOf`) — Rückfall auf den
 * Abschlusszeitpunkt, wenn keine Kennzahlen eingefroren wurden.
 */
export function datenstandBezugszeit(
  weekly: { status: "offen" | "abgeschlossen"; closedAt: string | null },
  metrics: { asOf: string } | null,
  jetzt: Date = new Date(),
): Date {
  if (weekly.status !== "abgeschlossen") return jetzt;
  if (metrics) return new Date(metrics.asOf);
  return weekly.closedAt ? new Date(weekly.closedAt) : jetzt;
}

/** ISO-Zeitpunkt → „06.10.2026, 07:03" (Datenstand-Zeilen). */
export function formatZeitpunkt(iso: string | null): string {
  return iso === null ? LEER : zeitpunkt.format(new Date(iso));
}

/**
 * Kompakter Restbudget-Wert einer Kundenzeile: der kleinste Prozentwert über
 * die Projekte mit Budget — ohne Pauschalen, bei denen der Zeitraum zählt und
 * nicht das Restbudget (#333). Hervorgehoben nach der globalen Regel — ein Projekt
 * des Kunden mit wenig Restbudget **oder** baldigem Ende; `lowBudget`/`endingSoon`
 * liefern den Grund als Text-Hinweis neben der Farbe.
 */
export function minBudgetProzent(
  rows: WeeklyBudgetRow[],
  customerId: string,
): { prozent: number | null; hervorheben: boolean; lowBudget: boolean; endingSoon: boolean } {
  const eigene = rows.filter((r) => r.customerId === customerId);
  const werte = eigene
    .filter((r) => r.billingKind !== "pauschale")
    .map((r) => r.budgetLeftPercent)
    .filter((p): p is number => p !== null);
  const lowBudget = eigene.some((r) => r.lowBudget && r.billingKind !== "pauschale");
  const endingSoon = eigene.some((r) => r.endingSoon);
  return {
    prozent: werte.length > 0 ? Math.min(...werte) : null,
    hervorheben: lowBudget || endingSoon,
    lowBudget,
    endingSoon,
  };
}
